import React, { useState } from 'react';
import { Mail, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline newsletter capture card. Lives between FAQ and Insurance on the homepage.
 * Single opt-in with explicit GDPR consent checkbox.
 */
const NewsletterCapture = () => {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email.');
      return;
    }
    if (!consent) {
      toast.error('Please tick the consent box to subscribe.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim(),
        consent: true,
        source: 'homepage_inline',
      });
      setSubscribed(true);
      toast.success("You're in! Check your inbox for a welcome note.");
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not subscribe. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="newsletter-section" data-testid="newsletter-section">
      <div className="section-container">
        <div className="newsletter-card">
          <div className="newsletter-icon-wrapper">
            <Mail className="newsletter-icon" size={28} />
          </div>

          {subscribed ? (
            <div className="newsletter-success" data-testid="newsletter-success">
              <CheckCircle2 size={48} className="newsletter-success-icon" />
              <h3 className="newsletter-success-title">You&rsquo;re subscribed</h3>
              <p className="newsletter-success-text">
                Thank you! Look out for Krina&rsquo;s monthly tip landing in your inbox soon.
                A welcome email is on its way to <strong>{email}</strong>.
              </p>
            </div>
          ) : (
            <>
              <h2 className="newsletter-title">Free Monthly Tips for Living Pain-Free</h2>
              <p className="newsletter-subtitle">
                One short, practical insight from Krina each month &mdash; TMD, jaw pain,
                headaches &amp; tinnitus. No spam. Unsubscribe anytime.
              </p>

              <form className="newsletter-form" onSubmit={handleSubmit} noValidate>
                <div className="newsletter-input-row">
                  <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    className="newsletter-input"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                    required
                    data-testid="newsletter-email-input"
                  />
                  <button
                    type="submit"
                    className="newsletter-submit-btn"
                    disabled={submitting}
                    data-testid="newsletter-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="newsletter-spin" size={18} />
                        Subscribing&hellip;
                      </>
                    ) : (
                      'Subscribe'
                    )}
                  </button>
                </div>

                <label className="newsletter-consent">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    disabled={submitting}
                    data-testid="newsletter-consent-checkbox"
                  />
                  <span>
                    I agree to receive emails from The TMD Physio. I can unsubscribe at any time.
                  </span>
                </label>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default NewsletterCapture;
